import React from 'react';
import { alpha, useTheme } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Chip from '@material-ui/core/Chip';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import TnoMediaLabLogo, { MEDIA_LAB_TAGLINES, CherryDevWordmark } from 'components/TnoMediaLabLogo';
import FloatingDecor from '../../CherrySoftware/components/FloatingDecor';
import IconBadge from '../../CherrySoftware/components/IconBadge';
import { hero } from '../annaSanelliaContent';
import ScalesIllustration from './ScalesIllustration';

const Hero = () => {
  const theme = useTheme();
  const { snapshot } = hero;

  return (
    <Box
      position="relative"
      overflow="hidden"
      px={{ xs: 2.5, md: 4 }}
      py={{ xs: 3, md: 4 }}
      sx={{
        background: `
          radial-gradient(ellipse 70% 55% at 10% 15%, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 55%),
          radial-gradient(ellipse 60% 45% at 95% 5%, ${alpha(theme.palette.secondary.main, 0.14)} 0%, transparent 50%),
          linear-gradient(165deg, ${theme.palette.alternate.dark} 0%, ${theme.palette.background.paper} 100%)
        `,
      }}
    >
      <FloatingDecor
        icons={[
          { id: 'gavel', icon: 'gavel', top: '12%', right: '8%', size: 40, color: 'primary', animate: true },
          { id: 'book', icon: 'menuBook', bottom: '18%', left: '6%', size: 36, color: 'secondary', animate: true, delay: '1.2s' },
          { id: 'event', icon: 'event', top: '28%', left: '12%', size: 32, color: 'primary', animate: true, delay: '0.6s' },
        ]}
      />

      <Grid container spacing={3} alignItems="stretch" position="relative" zIndex={1}>
        <Grid item xs={12} md={7}>
          <Box display="flex" flexDirection="column" justifyContent="center" height="100%" py={{ md: 1 }}>
            <TnoMediaLabLogo
              variant="cherry"
              tagline={MEDIA_LAB_TAGLINES.web}
              alt={hero.brand}
              height={{ xs: 44, md: 56 }}
              width="auto"
              sx={{ mb: 2 }}
            />

            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
              <Chip
                label="Propuesta personalizada"
                size="small"
                sx={{
                  fontWeight: 800,
                  bgcolor: alpha(theme.palette.secondary.main, 0.12),
                  color: 'secondary.dark',
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.25)}`,
                }}
              />
              <Chip
                label={hero.clientRole}
                size="small"
                icon={<Box component="span" sx={{ fontSize: 14, ml: 0.5 }}>⚖️</Box>}
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha(theme.palette.common.white, 0.92),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                }}
              />
            </Box>

            <Typography
              variant="h1"
              color="textPrimary"
              sx={{
                fontWeight: 800,
                lineHeight: 1.15,
                fontSize: { xs: '1.65rem', sm: '2rem', md: '2.35rem' },
                mb: 1.5,
                maxWidth: 540,
              }}
            >
              {hero.title}
            </Typography>

            <Typography
              color="textSecondary"
              sx={{
                lineHeight: 1.65,
                fontSize: { xs: '1rem', md: '1.05rem' },
                maxWidth: 520,
                mb: 2.5,
              }}
            >
              {hero.subtitle}
            </Typography>

            <Button
              variant="contained"
              color="secondary"
              size="large"
              href="#paquetes"
              endIcon={<ArrowForwardIcon />}
              sx={{
                px: 3.5,
                py: 1.35,
                borderRadius: 2,
                fontWeight: 800,
                alignSelf: 'flex-start',
                boxShadow: `0 12px 32px ${alpha(theme.palette.secondary.main, 0.35)}`,
              }}
            >
              {hero.cta}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box
            height="100%"
            borderRadius={4}
            position="relative"
            overflow="hidden"
            bgcolor={alpha(theme.palette.common.white, 0.94)}
            border={`1px solid ${alpha(theme.palette.primary.main, 0.1)}`}
            boxShadow={`0 24px 56px ${alpha(theme.palette.common.black, 0.07)}`}
            p={{ xs: 2.5, md: 3 }}
            sx={{
              backdropFilter: 'blur(14px)',
              transition: 'transform 0.25s ease, box-shadow 0.25s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 28px 64px ${alpha(theme.palette.primary.main, 0.12)}`,
              },
            }}
          >
            <Box
              position="absolute"
              top={-40}
              right={-40}
              width={160}
              height={160}
              borderRadius="50%"
              sx={{
                background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 70%)`,
                pointerEvents: 'none',
              }}
            />

            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2.5}
              position="relative"
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '16px',
                    display: 'grid',
                    placeItems: 'center',
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    color: 'common.white',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
                  }}
                >
                  {snapshot.initials}
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main', letterSpacing: 0.8 }}>
                    {snapshot.title}
                  </Typography>
                  <Typography sx={{ fontWeight: 800, lineHeight: 1.25, fontSize: '1.05rem' }}>
                    {hero.clientName}
                  </Typography>
                </Box>
              </Box>
              <ScalesIllustration size={72} />
            </Box>

            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                fontWeight: 800,
                color: 'secondary.main',
                px: 1.5,
                py: 0.5,
                mb: 2.5,
                borderRadius: 999,
                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.18)}`,
                gap: 0.5,
                fontSize: '0.75rem',
              }}
            >
              <CherryDevWordmark color="inherit" />
              <Box component="span"> · Web legal</Box>
            </Box>

            <Grid container spacing={1.5}>
              {snapshot.items.map((item) => (
                <Grid item xs={item.span || 6} key={item.id}>
                  <Box
                    sx={{
                      p: 1.75,
                      height: '100%',
                      borderRadius: 2.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.25,
                      transition: 'border-color 0.2s ease, background-color 0.2s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.07),
                        borderColor: alpha(theme.palette.primary.main, 0.18),
                      },
                    }}
                  >
                    <IconBadge icon={item.icon} size={36} variant="gradient" rounded="10px" />
                    <Box minWidth={0}>
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontWeight: 800, mt: 0.35, lineHeight: 1.3, fontSize: '0.95rem' }}>
                        {item.value}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}

              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 1.75,
                    borderRadius: 2.5,
                    bgcolor: alpha(theme.palette.secondary.main, 0.06),
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.14)}`,
                  }}
                >
                  <Typography variant="caption" color="secondary" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>
                    Objetivo del proyecto
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.75} mt={1}>
                    {snapshot.goalTags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{
                          fontWeight: 800,
                          bgcolor: alpha(theme.palette.common.white, 0.95),
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.22)}`,
                          color: 'text.primary',
                          '& .MuiChip-label': { px: 1.25 },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Hero;
